import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import { Copy, DownloadSimple } from '@phosphor-icons/react';
import parse from 'html-react-parser';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/ext-language_tools';
import ReactMarkdown from 'react-markdown';
import IconButtonTooltip from '../UI/IconButtonTooltip/IconButtonTooltip';
import { Segment } from '../UI/PreviewModal/PreviewModal';
import { ResponseDataGridTable } from '../UI/OutputTextarea/ResponseDataGridTable';
import { generateDataGridFromTable, extractImageUrl } from '../../util/markdown';
import { onDownloadFile, onDownloadHtmlContent } from '../../util/fileDownloader';
import { User } from '../../util/Types/User';

type Props = {
  segment: Segment;
  onCopy: (text: string) => void;
  user: User;
  segments: Segment[];
  index: number;
  value: string;
  autoPlay?: boolean;
};

const removeQuotesFromLink = (content: string): string => {
  return content.replace(/['"]/g, '');
};

const parseOptions = {
  replace: (domNode: any) => {
    if (domNode.name === 'img') {
      const { src, alt } = domNode.attribs;
      return (
        <Box
          component="img"
          src={src}
          alt={alt}
          sx={{
            maxWidth: '100%',
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          }}
        />
      );
    }
  },
};

const OutputMarkDown: React.FC<Props> = ({
  segment,
  onCopy,
  user,
  index,
  value,
}) => {
  const [active, setActive] = useState<'code' | 'preview'>('preview');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const contentIsHtml = /<[^>]+>/.test(segment.content);
  const isCodeSegment = segment.type === 'code';
  const isTableSegment = segment.type === 'table';
  const isImgSegment = segment.type === 'img';
  const isAudioSegment = segment.type === 'audio';
  const isLinkSegment = segment.type === 'link';

  useEffect(() => {
    if (isCodeSegment && contentIsHtml && active === 'preview' && iframeRef.current) {
      const htmlContent = `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <title>Preview</title>
        </head>
        <body>${segment.content}</body>
        </html>
      `;
      const iframe = iframeRef.current;
      if (iframe.contentDocument) {
        iframe.contentDocument.open();
        iframe.contentDocument.write(htmlContent);
        iframe.contentDocument.close();
      }
    }
  }, [segment.content, active, isCodeSegment, contentIsHtml]);

  const handleDownload = () => {
    if (isCodeSegment) {
      if (contentIsHtml) {
        onDownloadHtmlContent(segment.content, 'code');
      } else {
        onDownloadFile(segment.content, 'code');
      }
    } else if (isImgSegment) {
      onDownloadFile(extractImageUrl(segment.content), 'image');
    } else if (isAudioSegment) {
      onDownloadFile(extractImageUrl(segment.content), 'audio');
    }
  };

  const [boxSize, setBoxSize] = useState(0);
  const [isShowingFullSize, setIsShowingFullSize] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null)

  const handleOnToggleFullWidthClick = () => {
    setIsShowingFullSize(oldIsShowingFullSize => {
      if (boxRef.current) boxRef.current.style.height = oldIsShowingFullSize ? '400px' : `${boxSize}px`;

      return !oldIsShowingFullSize
    })
  }

  useEffect(() => {
    if (!iframeRef.current) return;
    
    const resizeObserver = new ResizeObserver(([entry]) => {
      const size = entry.borderBoxSize[0].inlineSize
      setBoxSize(Math.max(400, size));
    });

    resizeObserver.observe(iframeRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const renderContent = () => {
    if (isCodeSegment) {
      return (
        <Box
          sx={{
            width: '99%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '8px',
            border: '1px solid #D0D5DD',
            backgroundColor: user.theme === 'dark' ? '#1A1A2E' : '#FFF',
          }}
        >
          {contentIsHtml && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                backgroundColor: user.theme === 'dark' ? '#1A1A2E' : '#FFF',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                gap: '8px',
                padding: '4px 8px 8px 0',
              }}
            >
              <Button
                onClick={() => setActive('code')}
                sx={{
                  color: active === 'code' ? '#7e93c2' : '#737d8a',
                  textTransform: 'none',
                  fontWeight: active === 'code' ? 'bold' : 'normal',
                  borderBottom: active === 'code' ? '2px solid #a3bffa' : 'none',
                  borderRadius: 0,
                }}
              >
                Code
              </Button>
              <Button
                onClick={() => setActive('preview')}
                sx={{
                  color: active === 'preview' ? '#7e93c2' : '#737d8a',
                  textTransform: 'none',
                  fontWeight: active === 'preview' ? 'bold' : 'normal',
                  borderBottom: active === 'preview' ? '2px solid #a3bffa' : 'none',
                  marginRight: '16px',
                  borderRadius: 0,
                }}
              >
                Preview
              </Button>
              <IconButtonTooltip onClick={() => onCopy(segment.content)} title="Copy Code">
                <Copy color="#98A2B3" size={18} weight="bold" />
              </IconButtonTooltip>
              <IconButtonTooltip onClick={handleDownload} title="Download Code">
                <DownloadSimple color="#98A2B3" size={18} weight="bold" />
              </IconButtonTooltip>
            </Box>
          )}
          {isCodeSegment && contentIsHtml && active === 'preview' ? (
            <Box ref={boxRef} sx={{ position: 'relative', minHeight: '400px', width: '100%' }}>
              <iframe
                ref={iframeRef}
                title="Code Preview"
                style={{ minHeight: 'inherit', width: '100%', height: '100%', border: 'none', borderRadius: '8px' }}
              />
            </Box>
          ) : (
            <Box sx={{ position: 'relative' }}>
              <iframe
                ref={iframeRef}
                title="Code Preview"
                style={{ display: 'none' }}
              />
              <AceEditor
                mode="javascript"
                theme="monokai"
                name={`editor-${index}`}
                value={segment.content}
                fontSize={14}
                style={{
                  borderRadius: '8px',
                  width: '100%',
                }}
                lineHeight={19}
                showGutter={true}
                highlightActiveLine={true}
                maxLines={Infinity}
                minLines={1}
                setOptions={{
                  enableBasicAutocompletion: true,
                  enableLiveAutocompletion: true,
                  enableSnippets: true,
                  showLineNumbers: true,
                  tabSize: 2,
                  useWorker: false,
                }}
                readOnly
              />
            </Box>
          )}
          {contentIsHtml && active === 'preview' && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                backgroundColor: user.theme === 'dark' ? '#1A1A2E' : '#FFF',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                gap: '8px',
                padding: '4px 8px 8px 0',
              }}
            >
              <span style={{ flex:1 }}/>
              <Button
                onClick={handleOnToggleFullWidthClick}
                sx={{
                  color: '#7e93c2',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  borderBottom: '2px solid #a3bffa',
                  marginRight: '16px',
                  borderRadius: 0,
                }}
              >
                {isShowingFullSize ? 'Minimize -' : 'Maximize +'}
              </Button>
            </Box>
          )}
        </Box>
      );
    } else if (isTableSegment) {
      return (
        <Box sx={{ width: '100%', overflow: 'hidden' }}>
          <ResponseDataGridTable tableData={generateDataGridFromTable(segment.content)} />
        </Box>
      );
    } else if (isImgSegment) {
      return (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '8px',
            border: '1px solid #D0D5DD',
            backgroundColor: '#E9ECF0',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: '#E9ECF0',
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
              gap: '8px',
              paddingRight: '8px',
              paddingBottom: '8px',
              paddingTop: '4px',
            }}
          >
            <IconButtonTooltip onClick={() => onCopy(extractImageUrl(segment.content))} title="Copy image url">
              <Copy color="#98A2B3" size={18} weight="bold" />
            </IconButtonTooltip>
            <IconButtonTooltip onClick={handleDownload} title="Download Image">
              <DownloadSimple color="#98A2B3" size={18} weight="bold" />
            </IconButtonTooltip>
          </Box>
          {parse(segment.content, parseOptions)}
        </Box>
      );
    } else if (isAudioSegment) {
      return (
        <Box
          sx={{
            width: '68%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '8px',
            border: '1px solid #D0D5DD',
            backgroundColor: '#E9ECF0',
            padding: '8px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: '#E9ECF0',
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
              gap: '8px',
              paddingRight: '8px',
              paddingBottom: '6px',
              paddingTop: '4px',
            }}
          >
            <IconButtonTooltip onClick={() => onCopy(extractImageUrl(segment.content))} title="Copy audio url">
              <Copy color="#98A2B3" size={18} weight="bold" />
            </IconButtonTooltip>
            <IconButtonTooltip onClick={handleDownload} title="Download Audio">
              <DownloadSimple color="#98A2B3" size={18} weight="bold" />
            </IconButtonTooltip>
          </Box>
          {parse(segment.content, parseOptions)}
        </Box>
      );
    } else if (isLinkSegment) {
      const cleanedLink = removeQuotesFromLink(segment.content);
      return (
        <Box sx={{ padding: '15px 8px' }}>
          <a
            style={{
              wordBreak: 'break-all',
              color: user?.theme === 'dark' ? '#98A2B3' : '#3650DE',
            }}
            href={cleanedLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {cleanedLink}
          </a>
        </Box>
      );
    } else {
      const isJSON = (() => {
        try {
          const trimmed = segment.content.trim();
          return (trimmed.startsWith('{') && trimmed.endsWith('}')) || 
                 (trimmed.startsWith('[') && trimmed.endsWith(']'));
        } catch {
          return false;
        }
      })();

      if (isJSON) {
        return (
          <Box
            sx={{
              width: '99%',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '8px',
              border: '1px solid #D0D5DD',
              backgroundColor: user.theme === 'dark' ? '#1A1A2E' : '#FFF',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                backgroundColor: user.theme === 'dark' ? '#1A1A2E' : '#FFF',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                gap: '8px',
                padding: '4px 8px 8px 0',
              }}
            >
              <IconButtonTooltip onClick={() => onCopy(segment.content)} title="Copy JSON">
                <Copy color="#98A2B3" size={18} weight="bold" />
              </IconButtonTooltip>
            </Box>
            <Box sx={{ position: 'relative' }}>
              <AceEditor
                mode="json"
                theme="monokai"
                name={`json-editor-${index}`}
                value={segment.content}
                fontSize={14}
                style={{
                  borderRadius: '8px',
                  width: '100%',
                }}
                lineHeight={19}
                showGutter={true}
                highlightActiveLine={true}
                maxLines={Infinity}
                minLines={1}
                setOptions={{
                  enableBasicAutocompletion: true,
                  enableLiveAutocompletion: true,
                  enableSnippets: true,
                  showLineNumbers: true,
                  tabSize: 2,
                  useWorker: false,
                }}
                readOnly
              />
            </Box>
          </Box>
        );
      }

      return (
        <Box
          sx={{
            padding: '15px 8px',
            fontFamily: 'Roboto',
            fontSize: '16px',
            fontWeight: 400,
            color: user?.theme === 'dark' ? '#98A2B3' : 'black',
          }}
        >
          <ReactMarkdown>{segment.content || value}</ReactMarkdown>
        </Box>
      );
    }
  };

  return <Fragment>{renderContent()}</Fragment>;
};

export default OutputMarkDown;
